// src/pages/LoginPage.js
import React, { useState, useMemo } from "react";
import { auth } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { LOGO_SRC } from "../images";
import "./LoginPage.css";

// Self-assembling chessboard that fades its squares in on load — the
// page's signature moment. Pure visual; no interaction.
function AssemblingBoard() {
  const squares = useMemo(
    () => Array.from({ length: 64 }, (_, i) => {
      const row = Math.floor(i / 8);
      const col = i % 8;
      const dark = (row + col) % 2 === 1;
      return { id: i, dark, delay: (row + col) * 0.06 + Math.random() * 0.15 };
    }),
    []
  );
  return (
    <div className="login-board" aria-hidden="true">
      {squares.map(s => (
        <span
          key={s.id}
          className={`login-board-sq ${s.dark ? "dark" : "light"}`}
          style={{ animationDelay: `${s.delay}s` }}
        />
      ))}
    </div>
  );
}

// Big translucent pieces drifting across the screen — bold, playful motion.
const DRIFT_PIECES = ["♞", "♝", "♜", "♛", "♚", "♟", "♘", "♗"];
function DriftingPieces() {
  const pieces = useMemo(
    () => Array.from({ length: 10 }, (_, i) => ({
      id: i,
      glyph: DRIFT_PIECES[i % DRIFT_PIECES.length],
      top: Math.random() * 100,
      size: 48 + Math.random() * 90,
      duration: 22 + Math.random() * 22,
      delay: Math.random() * 25,
      opacity: 0.04 + Math.random() * 0.06,
    })),
    []
  );
  return (
    <div className="login-drift" aria-hidden="true">
      {pieces.map(p => (
        <span
          key={p.id}
          className="drift-piece"
          style={{
            top: `${p.top}%`,
            fontSize: `${p.size}px`,
            opacity: p.opacity,
            animationDuration: `${p.duration}s`,
            animationDelay: `-${p.delay}s`,
          }}
        >
          {p.glyph}
        </span>
      ))}
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError("Invalid email or password. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div className="login-bg">
      <div className="login-blob login-blob-1" aria-hidden="true" />
      <div className="login-blob login-blob-2" aria-hidden="true" />
      <AssemblingBoard />
      <DriftingPieces />

      <div className="login-container">
        <div className="login-logo">
          <img src={LOGO_SRC} alt="ChessingIt" />
        </div>
        <h1 className="login-title">ChessingIt</h1>
        <p className="login-sub">Make your move</p>

        <form onSubmit={handleLogin} className="login-form">
          <div className="login-field">
            <label>Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="login-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? (
              <span className="login-dots"><i/><i/><i/></span>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
